import React, { useState } from "react";

import { Spinner } from "@performyard/py-components";

import Button from "../../../../components/Button";
import { Grid, GridCol } from "../../../../components/Grid";
import Text from "../../../../components/Text";
import DisplayEditTeamSettingsModal from "./EditTeamSettingModal";
import DisplayTroubleShootModal from "./TroubleshootModal";

import useRequest from "../../../../hooks/useRequest";

import get from "../../../../utils/GetNested";
import Socket from "../../../../utils/Socket";

import PropTypes from "../../../../lib/prop-types";

import css from "../Teams.module.scss";

const TeamsIntegrationTable = (props) => {
	const {
		teamsList,
		installStatus,
		requestingInstallStatus,
		tenantID,
		teamsSettings,
		revalidateData,
		setJobInProgress,
	} = props;

	const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
	const [editTeamModalData, setEditTeamModalData] = useState({});

	const [isResyncModalOpen, setIsResyncModalOpen] = useState(false);
	const [resyncModalData, setResyncModalData] = useState({});

	const { revalidate: installApp, isRequesting: isRequestingInstall } = useRequest(
		"installApp",
		{},
		{ manual: true }
	);
	const { revalidate: updateSettings } = useRequest("updateTeamsSettings", {}, { manual: true });

	const selectTeamStatus = (teamID) => {
		const teamStatus = get(teamsSettings, [teamID, "status"], "disabled");
		return teamStatus;
	};

	const selectSettingsForTeam = (teamID) => {
		const settings = get(teamsSettings, [teamID], {});
		return settings;
	};

	const selectTeamDisplayName = (teamID) => {
		const selectedTeam = teamsList.filter((team) => team.id === teamID) || [];
		const teamDisplayName = get(selectedTeam, [0, "displayName"], "");
		return teamDisplayName;
	};

	const handleEditTeam = (teamID) => {
		setIsTeamModalOpen(true);
		setEditTeamModalData({
			teamID,
			teamDisplayName: selectTeamDisplayName(teamID),
			teamStatus: selectTeamStatus(teamID),
			settings: selectSettingsForTeam(teamID),
		});
	};

	const handleTeamInstall = async (teamID) => {
		setJobInProgress(true);
		await installApp({ filters: { tenantID, teamID } });

		const socket = Socket("/api/async_jobs/get_job_status/?name=install-microsoft-teams");
		try {
			socket.on("message", (message) => {
				if (message.data.completed !== true) {
					setJobInProgress(true);
				} else {
					setJobInProgress(false);
					socket.close();
					revalidateData();
				}
			});
		} catch {
			setJobInProgress(false);
			socket.close();
			revalidateData();
		}
	};

	const onSubmit = async (updates) => {
		await updateSettings({ filters: { updates } });
		revalidateData();
	};

	const handleConfirmResync = async (teamID) => {
		setIsResyncModalOpen(true);
		setResyncModalData({
			teamID,
			tenantID,
			teamDisplayName: selectTeamDisplayName(teamID),
		});
	};

	const isAppInstalledOnTeam = (teamID) => get(installStatus, [teamID], false);

	const getEditOrAddButton = (teamID) => {
		if (requestingInstallStatus) {
			return null;
		}

		if (isAppInstalledOnTeam(teamID)) {
			return (
				<div className={css.buttons}>
					<Button isHollow={true} onClick={() => handleEditTeam(teamID)}>
						Edit
					</Button>
				</div>
			);
		}

		return (
			<div className={css.buttons}>
				<Button color="blue" isRequesting={isRequestingInstall} onClick={() => handleTeamInstall(teamID)}>
					Add
				</Button>
			</div>
		);
	};

	const getResetButton = (teamID) => {
		if (requestingInstallStatus) {
			return null;
		}

		if (isAppInstalledOnTeam(teamID)) {
			return (
				<div className={css.buttons}>
					<Button color="red" onClick={() => handleConfirmResync(teamID)}>
						Reset
					</Button>
				</div>
			);
		}

		return null;
	};

	const getIndicators = (teamID) => {
		if (requestingInstallStatus) {
			return (
				<div className={css.spinnerContainer}>
					<Spinner size={24} />
				</div>
			);
		}
		if (isAppInstalledOnTeam(teamID)) {
			if (selectTeamStatus(teamID) === "enabled") {
				return (
					<div className={`${css.statusIndicator}`}>
						<div className={`${css.tableChip} ${css.blue}`}>
							<Text size="paragraph-3" weight="semi-bold">
								Installed
							</Text>
						</div>
						<div className={`${css.tableChip} ${css.green}`}>
							<Text size="paragraph-3" weight="semi-bold">
								Active
							</Text>
						</div>
					</div>
				);
			}

			return (
				<div className={`${css.statusIndicator}`}>
					<div className={`${css.tableChip} ${css.blue}`}>
						<Text size="paragraph-3" weight="semi-bold">
							Installed
						</Text>
					</div>
					<div className={`${css.tableChip} ${css.grey}`}>
						<Text size="paragraph-3" weight="semi-bold">
							Disabled
						</Text>
					</div>
				</div>
			);
		}
		return (
			<div className={`${css.statusIndicator}`}>
				<div className={`${css.tableChip} ${css.red}`}>
					<Text size="paragraph-3" weight="semi-bold">
						Not Installed
					</Text>
				</div>
			</div>
		);
	};

	return (
		<div>
			<Grid cols={8} className={css.columnLabels}>
				<GridCol span={3}>
					<div className={css.teamNameLabel}>
						<Text size="paragraph-3" weight="semi-bold">
							Team
						</Text>
					</div>
				</GridCol>
				<GridCol span={3}>
					<div className={css.statusLabel}>
						<Text size="paragraph-3" weight="semi-bold">
							Status
						</Text>
					</div>
				</GridCol>
				<GridCol span={2}>
					<div className={css.actionsLabel}>
						<Text size="paragraph-3" weight="semi-bold">
							Action
						</Text>
					</div>
				</GridCol>
			</Grid>
			{teamsList.map((team) => {
				const { displayName = "", id } = team;

				return (
					<div className={css.teamRow} key={id}>
						<Grid cols={8}>
							<GridCol span={3}>
								<div className={css.teamName}>
									<Text size="paragraph-3" weight="regular">
										{displayName}
									</Text>
								</div>
							</GridCol>

							<GridCol span={3}>{getIndicators(id)}</GridCol>

							<div className="flex gap-[4px]">
								<div>{getEditOrAddButton(id)}</div>
								<div>{getResetButton(id)}</div>
							</div>
						</Grid>
					</div>
				);
			})}
			{isTeamModalOpen ? (
				<DisplayEditTeamSettingsModal
					handleClose={() => setIsTeamModalOpen(false)}
					data={editTeamModalData}
					onSubmit={(...args) => onSubmit(...args)}
					openResyncModal={(...args) => handleConfirmResync(...args)}
				/>
			) : null}

			{isResyncModalOpen ? (
				<DisplayTroubleShootModal
					handleClose={() => setIsResyncModalOpen(false)}
					data={resyncModalData}
					handleRefresh={() => revalidateData()}
				/>
			) : null}
		</div>
	);
};

TeamsIntegrationTable.propTypes = {
	teamsList: PropTypes.array.isRequired,
	teamsSettings: PropTypes.object.isRequired,
	tenantID: PropTypes.string.isRequired,
	installStatus: PropTypes.object.isRequired,
	requestingInstallStatus: PropTypes.bool.isRequired,
	setJobInProgress: PropTypes.func.isRequired,
	revalidateData: PropTypes.func.isRequired,
};

export default TeamsIntegrationTable;
